import {
  Upload as UploadIcon,
  Help as HelpIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Divider,
  Alert,
  AlertTitle,
} from "@mui/material";
import React, { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import GenericTable from "0.Mine/0.Components/Reusable/GenericTable";
import { Lead, LeadState } from "0.Mine/3.Interfaces/lead";

const LEAD_FIELDS = [
  "name",
  "company",
  "email",
  "phone",
  "state",
  "leadSource",
  "notes",
  "value",
] as const;

export interface ImportConfigurationProps {
  importMethod: "csv" | "manual";
  csvFile: File | null;
  onFileUpload: (event: ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  columnMapping: Record<string, string>;
  onMappingChange: (csvColumn: string, leadField: string) => void;
  previewData: any[];
  totalRowCount: number;
  isLoading: boolean;
  manualLeads: Partial<Lead>[];
  onManualLeadChange: (index: number, field: keyof Lead, value: any) => void;
  onAddManualLead: () => void;
  onRemoveManualLead: (index: number) => void;
}

export const ImportConfiguration: React.FC<ImportConfigurationProps> = ({
  importMethod,
  csvFile,
  onFileUpload,
  fileInputRef,
  columnMapping,
  onMappingChange,
  previewData,
  totalRowCount,
  isLoading,
  manualLeads,
  onManualLeadChange,
  onAddManualLead,
  onRemoveManualLead,
}) => {
  const { t } = useTranslation();
  if (importMethod === "csv") {
    return (
      <Box>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("leads.uploadCSVFile")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {csvFile ? t("leads.changeFile") : t("leads.selectFile")}
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={onFileUpload}
                accept=".csv,.xls,.xlsx, .tsv, .txt"
                style={{ display: "none" }}
              />
              {csvFile && <Typography>Selected: {csvFile.name}</Typography>}
              <Tooltip title={t("leads.supportedFormats")}>
                <IconButton size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
        {csvFile && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("leads.mapColumns")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t("leads.matchColumns")}
              </Typography>
              <Grid container spacing={2}>
                {LEAD_FIELDS.map((csvColumn) => (
                  <Grid item xs={12} sm={6} md={4} key={csvColumn}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{csvColumn}</InputLabel>
                      <Select
                        value={columnMapping[csvColumn]}
                        label={csvColumn}
                        onChange={(e) => onMappingChange(csvColumn, e.target.value as string)}
                      >
                        {Object.keys(columnMapping).map((csvOption) => (
                          <MenuItem key={csvOption} value={csvOption}>
                            {csvOption}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                Preview {totalRowCount > 20 && `(Showing first 20 of ${totalRowCount} rows)`}
              </Typography>
              <Box sx={{ height: 300, position: "relative" }}>
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      border: "1px dashed #ccc",
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <CircularProgress size={40} sx={{ mb: 2 }} />
                      <Typography>Processing your file...</Typography>
                    </Box>
                  </Box>
                ) : (
                  <>
                    {previewData && previewData.length > 0 ? (
                      <>
                        <GenericTable columns={Object.keys(previewData[0])} data={previewData} />
                        {totalRowCount > 20 && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            * Preview limited to 20 rows for performance. All {totalRowCount} rows
                            will be imported.
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          border: "1px dashed #ccc",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {t("leads.noData")}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    );
  } else if (importMethod === "manual") {
    return (
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">{t("leads.manualEntry")}</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAddManualLead}>
              Aggiungi riga
            </Button>
          </Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>{t("leads.excelLike")}</AlertTitle>
              {t("leads.addLeads")}
            </Alert>
            <Box sx={{ overflow: "auto" }}>
              <Box sx={{ minWidth: 800 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px",
                    gap: 1,
                    bgcolor: "#f8f9fa",
                    p: 1,
                    borderRadius: "4px 4px 0 0",
                    fontWeight: "bold",
                  }}
                >
                  <Typography variant="body2">{t("leads.name")}</Typography>
                  <Typography variant="body2">{t("leads.company")}</Typography>
                  <Typography variant="body2">{t("leads.email")}</Typography>
                  <Typography variant="body2">{t("leads.phone")}</Typography>
                  <Typography variant="body2">{t("leads.state")}</Typography>
                  <Typography variant="body2">{t("leads.source")}</Typography>
                  <Typography variant="body2">{t("leads.value")}</Typography>
                  <Box />
                </Box>
                <Divider />
                {manualLeads.map((lead, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px",
                      gap: 1,
                      p: 1,
                      bgcolor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                      "&:hover": { bgcolor: "#f1f4f8" },
                    }}
                  >
                    <TextField
                      size="small"
                      value={lead.name || ""}
                      onChange={(e) => onManualLeadChange(index, "name", e.target.value)}
                      placeholder={t("leads.name")}
                    />
                    <TextField
                      size="small"
                      value={lead.company || ""}
                      onChange={(e) => onManualLeadChange(index, "company", e.target.value)}
                      placeholder={t("leads.company")}
                    />
                    <TextField
                      size="small"
                      value={lead.email || ""}
                      onChange={(e) => onManualLeadChange(index, "email", e.target.value)}
                      placeholder={t("leads.email")}
                    />
                    <TextField
                      size="small"
                      value={lead.phone || ""}
                      onChange={(e) => onManualLeadChange(index, "phone", e.target.value)}
                      placeholder={t("leads.phone")}
                    />
                    <FormControl size="small" fullWidth>
                      <Select
                        value={lead.state || LeadState.New}
                        onChange={(e) => onManualLeadChange(index, "state", e.target.value)}
                      >
                        {Object.values(LeadState).map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      value={lead.leadSource || ""}
                      onChange={(e) => onManualLeadChange(index, "leadSource", e.target.value)}
                      placeholder={t("leads.source")}
                    />
                    <TextField
                      size="small"
                      value={lead.value || ""}
                      onChange={(e) => onManualLeadChange(index, "value", e.target.value)}
                      placeholder={t("leads.value")}
                    />
                    <IconButton
                      color="error"
                      onClick={() => onRemoveManualLead(index)}
                      disabled={manualLeads.length === 1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {manualLeads.length} {t("leads.leadsToImport")}
              </Typography>
            </Box>
            <Tooltip title={t("leads.pressTab")}>
              <IconButton>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    );
  }
  return null;
};
